/**
 * The tenant's **Corner radius**, resolved per surface family.
 *
 * VRG-562. A brand sets Corner radius in Studio, looks at the two screens
 * Studio opens on, and — before this file existed — saw nothing change. Measured
 * on the certificates list: **47 visible elements had a corner radius and none of
 * them moved** when the token went 20 -> 2. They computed 6/8/12/999px regardless.
 * Every element that DID follow the token sat inside a closed drawer. The control
 * was, for the surfaces that matter, dead.
 *
 * The cause was `tokens.ts`'s `radius` scale: absolute pixels, correct for chrome,
 * wrong for the product's own cards. This module is the tenant-driven half of that
 * split. `radius` stays for corners a brand must not control.
 *
 * ---
 *
 * ## The scale, and why these three multipliers and not others
 *
 * Three steps, because the app has exactly three surface depths and concentric
 * radii must decrease inward (`packages/ui/theme/shape.ts`: "an inner box at the
 * outer radius reads as misaligned"):
 *
 * | step        | multiplier | today's literal | what it is |
 * |-------------|-----------:|----------------:|------------|
 * | `innerPx`   |     × 1.0  |             8px | a thumbnail, a nested row, a segment inside a card |
 * | `surfacePx` |     × 1.5  |            12px | a CARD — the certificate card, a panel, a popover |
 * | `containerPx`|    × 2.0  |            16px | a sheet, a modal, a hero image, the thing cards sit in |
 *
 * **The multipliers are derived, not chosen.** The app's effective default token is
 * **8** (`createMuiTheme`'s `baseRadius`; measured in the database, 4 of 6 companies
 * store nothing and 1 stores exactly 8). Pinning the three steps to their existing
 * literals at that default — 8/12/16 — fixes the ratios at 1 / 1.5 / 2 with no
 * freedom left. That is the point: **at the default token this file renders the app
 * pixel-identically to before.** A tenant who never touched the control sees no
 * change; the ticket was "the control does nothing", not "the app's radii are wrong",
 * and silently reflattening every card for every existing tenant would be scope
 * nobody asked for.
 *
 * ## Why proportional rather than additive
 *
 * `surface = token + 4` would also hit 12 at the default, and it is wrong at one
 * end that matters: a brand who drags the slider to **Sharp (0) wants square**, and
 * additive gives them 4px cards beside 0px buttons — the exact mismatch they were
 * avoiding. Multiplication takes 0 to 0 on every step. (Same argument
 * `fieldMetrics.ts` makes for clamping rather than fixing the field radius.)
 *
 * ## Where a large value WOULD deform, and the one clamp that stops it
 *
 * Radius does not scale with the element — `fieldMetrics.ts` records this at
 * length, and it is why a field clamps to 6px while a button takes the token
 * verbatim. The same geometry applies here, but it bites only on SMALL FIXED-SIZE
 * boxes: `innerPx` at the top of the range is 24px, which is nothing on a 343px
 * panel and a blob on a 48px thumbnail. So the clamp is size-aware rather than a
 * flat ceiling — see {@link tilePx}. Large surfaces need no ceiling: `containerPx`
 * tops out at 48px on a full-width sheet, which reads as a deliberate "Rounded",
 * not as damage.
 *
 * ## What this deliberately does NOT touch
 *
 * Pills (`999`), circles (`50%`), status chips, avatars, count badges, checkbox
 * and radio glyphs, loading dots, flag thumbnails, scroll thumbs. A chip at radius
 * 2 is not a brand choice, it is a broken chip; an avatar that stops being round
 * stops being an avatar. Those keep `tokens.ts`'s `radius` or their literal.
 *
 * ## Using it
 *
 * Every helper is an MUI `sx` property callback, so the common case is a direct
 * reference with no wrapper:
 *
 *     sx={{ borderRadius: surfaceRadius }}          // was '12px'
 *     sx={{ borderRadius: innerRadius }}            // was '8px'
 *     sx={{ borderRadius: containerRadius }}        // was '16px'
 *     sx={{ borderRadius: tileRadius(48) }}         // was '8px', on a 48px square
 *
 * Inside a larger `sx` callback that already has `theme`, use the `*Px` numeric
 * form: `borderRadius: `${surfacePx(theme.shape.borderRadius)}px``.
 */

/** The app-wide fallback when a tenant has no `border_radius` — `createMuiTheme`'s `baseRadius`. */
const DEFAULT_TOKEN = 8;

/**
 * ⚠️ **SURFACES NO LONGER FOLLOW THE TENANT'S TOKEN. This resolver ignores its
 * argument and returns the base, by decision — 2026-10-30, VRG-720.**
 *
 * Studio's control is "Border radius — how rounded your BUTTONS are". Spencer's
 * call, after seeing it driven to the top of the range in the preview: it should
 * round buttons and fields, not cards. At token 24 the certificate card's action
 * tiles computed 36px and the cards 36/12px, and a 36px corner on a 343px panel
 * reads as a different product rather than as the same product with rounder
 * buttons.
 *
 * This is deliberately the OPPOSITE of what VRG-562 built this module for ("the
 * control was, for the surfaces that matter, dead"). That ticket was right that
 * nothing moved and wrong about which things should: the fix it wanted was for
 * BUTTONS to move, and they do — `createMuiTheme`'s `MuiButton` takes the token
 * verbatim, and `fieldMetrics` takes it clamped. Cards were collateral.
 *
 * The scale and every call site are left intact rather than inlined back to
 * literals, so reversing this is a one-line change here. Restore by returning
 * the finite-checked `n` below.
 *
 * NOTE this does NOT govern everything with a corner: `theme.shape.borderRadius`
 * is read directly by the drawers, bottom nav, menus and page headers, and those
 * still follow the tenant. If those should stop too, that is a separate change at
 * `createMuiTheme`'s `shape`, not here.
 */
const token = (_themeRadius: unknown): number => DEFAULT_TOKEN;

/** Half-pixel corners are invisible and make measurement noisy; round to whole pixels. */
const step = (themeRadius: unknown, multiplier: number): number =>
  Math.round(token(themeRadius) * multiplier);

/** A thumbnail, a nested row, a segment INSIDE a card. 8px at the default token. */
export const innerPx = (themeRadius: unknown): number => step(themeRadius, 1);

/** A card, panel or popover — the app's default surface. 12px at the default token. */
export const surfacePx = (themeRadius: unknown): number => step(themeRadius, 1.5);

/** A sheet, modal or hero image — the thing cards sit in. 16px at the default token. */
export const containerPx = (themeRadius: unknown): number => step(themeRadius, 2);

/**
 * `innerPx`, clamped so it cannot swallow a small fixed-size box.
 *
 * A radius of a quarter the box's side is the point past which a square stops
 * reading as a rounded square and starts reading as a circle that failed. At the
 * default token the clamp is inert for everything above 32px, so no existing
 * thumbnail moves; it engages only where the brand's value would deform the shape.
 *
 * @param sizePx the box's smaller side, in CSS pixels.
 */
export const tilePx = (themeRadius: unknown, sizePx: number): number =>
  Math.min(innerPx(themeRadius), Math.round(sizePx / 4));

type Themeish = { shape?: { borderRadius?: unknown } };

/** `sx={{ borderRadius: innerRadius }}` */
export const innerRadius = (theme: Themeish): string => `${innerPx(theme?.shape?.borderRadius)}px`;

/** `sx={{ borderRadius: surfaceRadius }}` */
export const surfaceRadius = (theme: Themeish): string =>
  `${surfacePx(theme?.shape?.borderRadius)}px`;

/** `sx={{ borderRadius: containerRadius }}` */
export const containerRadius = (theme: Themeish): string =>
  `${containerPx(theme?.shape?.borderRadius)}px`;

/** `sx={{ borderRadius: tileRadius(48) }}` — see {@link tilePx}. */
export const tileRadius =
  (sizePx: number) =>
  (theme: Themeish): string =>
    `${tilePx(theme?.shape?.borderRadius, sizePx)}px`;

/**
 * A surface rounded on SOME corners only — the list-mode card's flush-left photo,
 * a segmented control's end caps.
 *
 * Takes the four corners as multiples of the resolved step so a partial rounding
 * cannot drift from the whole one: `edgeRadius(surfacePx)('1 0 0 1')` is the card's
 * radius on the left pair and square on the right, whatever the tenant chose.
 */
export const edgeRadius =
  (
    resolve: (themeRadius: unknown) => number,
    corners: [number, number, number, number],
    /**
     * How far INSIDE the surface this element sits, in px — almost always the
     * parent's border width.
     *
     * Concentric radii must decrease inward (this file's header), and a 1px
     * border is an inward step like any other: a card with `borderRadius: 12`
     * and `border: 1px` has a PADDING BOX whose corners curve at 11, so a child
     * flush against it needs 11, not 12.
     *
     * Ask for 12 and the child's arc is ROUNDER than the hole it sits in, so it
     * pulls away from the corner and the card's own background shows through as
     * a white wedge. That is what the full-bleed certificate photo was doing,
     * and it is why this parameter exists rather than each call site subtracting
     * a literal — the arithmetic has to survive the tenant moving the token.
     *
     * Clamped at 0, so a brand on Sharp (token 0) gets square rather than
     * negative.
     */
    insetPx = 0
  ) =>
  (theme: Themeish): string => {
    const r = Math.max(0, resolve(theme?.shape?.borderRadius) - insetPx);
    return corners.map(c => `${Math.round(r * c)}px`).join(' ');
  };
